'use client'

import { type CSSProperties, Fragment, useContext, useEffect } from 'react'

import {
    Tooltip as AriakitTooltip,
    TooltipAnchor,
    type TooltipOptions,
    type TooltipState,
    useTooltipState,
} from 'ariakit/tooltip'
import cn from 'clsx'
import { Text } from '@/components/Text/Text'
import { ThemeContext } from '@/utils/themeContext'

import { useCursorTooltipState } from './use-cursor-tooltip-state'

import css from './Tooltip.module.css'

export interface TooltipProps extends Omit<TooltipOptions, 'state'> {
    title: React.ReactNode | string
    children: React.ReactNode
    className?: string
    placement?: TooltipState['placement']
    followCursor?: boolean
}
interface FollowCursorCustomProperties extends CSSProperties {
    '--tooltip-transform': string
}

export function Tooltip({
    title,
    children,
    className,
    placement = 'top',
    followCursor = false,
    ...props
}: TooltipProps) {
    const { theme = 'todoist' } = useContext(ThemeContext)
    const tooltip = useTooltipState({
        placement,
        gutter: 2,
    })

    // this `transform` value is provided by popover.js's updated state.
    // it is used to position the tooltip relative to the cursor via css custom properties
    const { transform, ...followingTooltip } = useCursorTooltipState({
        placement,
        gutter: 20,
    })

    useEffect(() => {
        tooltip.placement = placement
    }, [tooltip, placement])

    const tooltipChildren: React.ReactNode =
        typeof title === 'string' ? (
            <Text tag="span" size="body-2xs">
                {title}
            </Text>
        ) : (
            title
        )

    return (
        <Fragment>
            <TooltipAnchor
                state={followCursor ? followingTooltip : tooltip}
                className={cn(css.anchor, className)}
            >
                {children}
            </TooltipAnchor>

            <AriakitTooltip
                className={cn([css.tooltip, css[theme], { [css.followCursor]: followingTooltip }])}
                state={followCursor ? followingTooltip : tooltip}
                style={{ '--tooltip-transform': transform } as FollowCursorCustomProperties}
                {...props}
            >
                {tooltipChildren}
            </AriakitTooltip>
        </Fragment>
    )
}
